import Stepper from "@/components/Stepper";
import { Input } from "@/components/ui/input";
import { Check, ArrowRight, Info } from "lucide-react";
import { FC, useState } from "react";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from "@/components/ui/form";
import axios from "axios";
import { useToast } from "@/components/ui/use-toast";
import Card from "@/components/Card";
import { useError } from "@/contexts/ErrorProvider";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layouts/Dashboard";
import config from "@/config";

// --- Define the schema ---
const ProjectInfoSchema = z.object({
  mainPackage: z
    .string()
    .min(5, "Metadata must be greater than 5 characters")
    .max(100, "Metadata must be less than 100 characters"),
  projectName: z
    .string()
    .min(5, "Title must be greater than 5 characters")
    .max(100, "Title must be less than 100 characters"),
  projectType: z.string().default("PROJECT")
});

type CarouselStep = 1 | 2;

const steps = ["Project Info", "Specs", "Generate and Refine"];

// --- New: TechStackSelector component ---
interface TechStack {
  id: number;
  title: string;
  stackName: string;
  available: boolean;
  language: { logo: string; text: string };
  framework: { logo: string; text: string };
  extras: { logo?: string; text: string }[];
}

const techStacks: TechStack[] = [
  {
    id: 0,
    title: "JAVA / SPRING Stack",
    stackName: "java-spring",
    available: true,
    language: { logo: "/images/java2.png", text: "Java" },
    framework: { logo: "/images/springboot.png", text: "Spring Boot" },
    extras: [
      { logo: "/images/hibernate.jpg", text: "Hibernate" },
      { logo: "/images/maven.png", text: "Maven" },
      { logo: "/images/junit.png", text: "JUnit" },
      { logo: "/images/etc2.png", text: "" } // Image-only "etc"
    ]
  },
  {
    id: 1,
    title: "Django Stack",
    stackName: "python-django",
    available: false,
    language: { logo: "/images/python.jpeg", text: "Python" },
    framework: { logo: "/images/django.png", text: "Django" },
    extras: [
      { logo: "/images/postgres.png", text: "PostgreSQL" },
      { logo: "/images/redis.png", text: "Redis" },
      { logo: "/images/sentry.png", text: "Sentry" },
      { logo: "/images/etc2.png", text: "" }
    ]

  },
  {
    id: 5,
    title: "Node.js / Express Stack",
    stackName: "nodejs-express",
    available: false,
    language: { logo: "/images/node.png", text: "Node.js" },
    framework: { logo: "/images/express.png", text: "Express" },
    extras: [
      { logo: "/images/mongo.png", text: "MongoDB" },
      { logo: "/images/redis.png", text: "Redis" },
      { logo: "/images/rabbitmq.png", text: "RabbitMQ" },
      { logo: "/images/etc2.png", text: "" }
    ]
  },
  {
    id: 3,
    title: ".NET Core Stack",
    stackName: "dotnet-core",
    available: false,
    language: { logo: "/images/csharp.png", text: "C#" },
    framework: { logo: "/images/dotnet.jpeg", text: ".NET Core" },
    extras: [
      { logo: "/images/entityframework.png", text: "Entity Framework" },
      { logo: "/images/mssq.png", text: "MS SQL" },
      { logo: "/images/postgres.png", text: "MS SQL" },
      { logo: "/images/etc2.png", text: "" }
    ]
  }
];

interface TechStackSelectorProps {
  selected: number;
  onSelect: (id: number, stackName: string, available: boolean) => void;
}

const TechStackSelector: FC<TechStackSelectorProps> = ({ selected, onSelect }) => {
  return (
    <div className="w-full mb-6 mt-6">
      {techStacks.map((stack) => {
        const isSelected = selected === stack.id;
        return (
          <div
            key={stack.id}
            onClick={() => onSelect(stack.id, stack.stackName, stack.available)}
            className={`relative w-full border-4 rounded-lg px-5 py-4 mb-4 cursor-pointer bg-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.01] ${
              isSelected ? "border-green-600 shadow-xl scale-[1.02]" : "border-gray-300"
            }`}
          >
            {/* Selected check icon */}
            {isSelected && (
              <div className="absolute top-3 right-3 text-green-600">
                <Check className="w-8 h-8" />
              </div>
            )}
            <div className="mb-3">
              <h2 className="font-bold text-xl">{stack.title}</h2>
            </div>
            <div className="flex items-stretch">
              {/* Left compartment: Language */}
              <div className="flex flex-col flex-1 items-center justify-center">

                <div className="w-full h-20 min-h-[150px] flex items-center justify-center">
                  <img src={stack.language.logo} alt={stack.language.text}
                       className="max-w-full max-h-full object-contain" />
                </div>
              </div>
              <div className="w-px bg-gray-300 mx-4" />
              {/* Middle compartment: Framework */}
              <div className="flex flex-col flex-1 items-center justify-center">

                <div className="w-full h-20 flex items-center justify-center">
                  <img src={stack.framework.logo} alt={stack.framework.text}
                       className="max-w-full max-h-full object-contain" />
                </div>
              </div>
              <div className="w-px bg-gray-300 mx-4" />
              {/* Right compartment: Other */}
              <div className="flex flex-col flex-1 items-center justify-center">

                <div className="w-full h-30 grid grid-cols-2 gap-2">
                  {stack.extras.map((tech, idx) => (
                    <div key={idx} className="w-full h-full flex items-center">
                      {tech.logo ? (
                        <img src={tech.logo} alt={tech.text} className="w-full h-full object-contain" />
                      ) : (
                        <img src="/images/ecosystem-tools.png" alt="Ecosystem Tools"
                             className="w-full h-full object-contain" />
                      )}
                    </div>
                  ))}
                </div>

              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Main Create Page ---
interface CreateProps {
}

const Create: FC<CreateProps> = () => {
  const { toast } = useToast();
  const { setIsError } = useError();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<CarouselStep>(1);
  const [selectedStack, setSelectedStack] = useState<number>(0);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  const form = useForm<z.infer<typeof ProjectInfoSchema>>({
    resolver: zodResolver(ProjectInfoSchema),
    defaultValues: {
      mainPackage: "ai.shreds",
      projectName: "",
      projectType: "PROJECT"
    }
  });

  async function onSubmit(project: z.infer<typeof ProjectInfoSchema>) {
    setIsError("");

    try {
      await createProject(project);

    } catch (err: any) {
      handleSubmitError(err);
    }
  }

  const createProject = async (project: z.infer<typeof ProjectInfoSchema>) => {
    try {
      const res = await axios.post(`${config.NEXT_PUBLIC_BASE_URL}/api/projects`, project, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("accessToken")
        }
      });

      // Success case
      const data = { projectId: res.data.id };
      navigate(`/architecture/specs/${data.projectId}`);

    } catch (err: any) {
      handleSubmitError(err);
    }
  };

  const handleSubmitError = (err: any) => {
    if (err.response?.data?.body?.message) {
      setIsError(err.response.data.body.message);
    } else if (err.response?.data?.message) {
      setIsError(err.response.data.message);
    } else if (err.response?.status === 400) {
      toast({
        title: "400",
        description: "Please fill all the required fields.",
        className: "bg-red-500 text-white"
      });
    } else {
      setIsError("Sorry something went wrong. Please try again later.");
    }
  };

  const handleNextFromStep1 = () => {
    const projectName = form.getValues("projectName");
    if (!projectName || projectName.length < 5) {
      form.setError("projectName", {
        type: "manual",
        message: "Title must be greater than 5 characters"
      });
      return;
    }
    setCurrentStep(2);
  };

  const handleNextFromStep2 = async () => {
    // Validate form and submit
    const isValid = await form.trigger(["projectName", "mainPackage"]);
    if (isValid) {
      form.handleSubmit(onSubmit)();
    }
  };

  const handleStackSelection = async (id: number, stackName: string, available: boolean) => {
    // Only set as selected if available (Java only)
    if (available) {
      setSelectedStack(id);
    }

    // Call API for statistics (non-blocking)
    try {
      await axios.post(`${config.NEXT_PUBLIC_BASE_URL}/api/stack/${stackName}`, {}, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("accessToken")
        }
      });
    } catch (err) {
      // Silently fail - this is just for statistics
      console.log("Stack statistics tracking failed (non-critical):", err);
    }

    // Show modal for unavailable stacks
    if (!available) {
      setShowUnavailableModal(true);
    }
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl m-auto flex flex-col items-stretch gap-5 h-full w-full grow">
        <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="full" grow maxWidth="w-full" mAuto>
          <div className="m-auto gap-5 h-full w-full p-1 xl:p-3 lg:px-0 px-2">
            <div className="w-[100%] sm:w-[90%] md:w-2/3 min-w-[250px] m-auto mt-3">
              <Stepper steps={steps} step={1} />

              <Form {...form}>
                <form id="project-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">

                  {/* Step 1: Project Name */}
                  {currentStep === 1 && (
                    <div className="min-h-[500px] flex flex-col items-center justify-center py-12 animate-fadeIn">
                      <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                        What's your project name?
                      </h2>
                      <FormField
                        control={form.control}
                        name="projectName"
                        render={({ field }) => (
                          <FormItem className="w-full">
                            <FormControl>
                              <Input
                                placeholder="Enter your project name"
                                {...field}
                                className="text-2xl py-8 px-6 text-center border-2 border-gray-300 focus:border-blue-500 rounded-xl shadow-lg transition-all"
                              />
                            </FormControl>
                            <FormMessage className="text-center mt-3" />
                          </FormItem>
                        )}
                      />
                      <button
                        type="button"
                        onClick={handleNextFromStep1}
                        className="mt-12 px-12 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-3"
                      >
                        Next
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {/* Step 2: Tech Stack Selection */}
                  {currentStep === 2 && (
                    <div className="min-h-[500px] flex flex-col py-8 animate-fadeIn">
                      {/* Top Next Button */}
                      <div className="flex justify-end mb-4">
                        <button
                          type="button"
                          onClick={handleNextFromStep2}
                          className="px-12 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-3"
                        >
                          Next
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>

                      <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">
                        Choose your tech stack
                      </h2>
                      <p className="text-gray-500 text-center mb-8">Select the technology stack for your project</p>
                      <TechStackSelector selected={selectedStack} onSelect={handleStackSelection} />

                      {/* Bottom Next Button */}
                      <div className="flex justify-center mt-6">
                        <button
                          type="button"
                          onClick={handleNextFromStep2}
                          className="px-12 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-3"
                        >
                          Next
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Unavailable Stack Modal */}
                  {showUnavailableModal && (
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                      <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowUnavailableModal(false)}></div>
                      <div className="bg-white rounded-2xl overflow-hidden shadow-2xl transform transition-all max-w-md w-full mx-4 z-10">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-8 text-center">
                          <div className="flex justify-center mb-4">
                            <div className="bg-white rounded-full p-4">
                              <Info className="w-12 h-12 text-blue-600" />
                            </div>
                          </div>
                          <h3 className="text-2xl font-bold text-white mb-2">Coming Soon!</h3>
                        </div>
                        <div className="px-6 py-6 text-center">
                          <p className="text-gray-700 text-lg mb-6">
                            This tech stack is currently unavailable and will be ready in a few weeks.
                          </p>
                          <p className="text-gray-600 mb-8">
                            Only the <span className="font-bold text-green-600">JAVA / SPRING</span> stack is available right now. Why not give it a try?
                          </p>
                          <button
                            type="button"
                            className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                            onClick={() => setShowUnavailableModal(false)}
                          >
                            Got it!
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                </form>
              </Form>

            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Create;